import { useCallback, useRef } from 'react'
import { useParams } from 'react-router-dom'
import Form, { AjvError, UiSchema } from '@rjsf/core'
import { JSONSchema7 } from 'json-schema'

import Editor from '../editor/Editor'

import './Form.css'
import styles from './ComponentForm.module.css'
import { Box, Button } from '@mui/material'
import { getHeaders } from '../../utils/common'

export type TransformError = {
  name: string
}

function transformErrors(errors: AjvError[]) {
  // If items exist in the provided form data that aren't within the schema then
  // there is still no valid case where we would want to expose that as an error
  // in the form.
  return errors.filter((error) => {
    return error.name !== 'additionalProperties'
  })
}

interface ComponentFormParams {
  schema: JSONSchema7
  uiSchema: UiSchema
  formData: any
  disabled: boolean
  onChange: (ctx: any) => void
  onSubmit: (ctx: any) => void
  onCancel: () => void
}

type appParams = {
  sessionID: string
}

export default function ComponentForm({ schema, uiSchema, formData, disabled, onChange, onSubmit, onCancel }: ComponentFormParams) {
  const { sessionID } = useParams<appParams>()

  const myHeaders = getHeaders()
  myHeaders.append('Content-Type', 'application/json')

  const lintBloblang = useCallback(
    (saveContent:any, onSave:any, onError:any) => {
      fetch(`/mqttapi/v1/session/${sessionID}/compile/blobl`, {
        method: 'POST',
        headers: myHeaders,
        body: saveContent,
      })
        .then(async (response) => {
          if (response.ok) {
            return response.json()
          }
          const text = await response.text()
          throw new Error(`${response.statusText}: ${text}`)
        })
        .then((json) => {
          json.lints = [{ line: json.Line, what: json.Text ?? '' }]
          onSave(json.lints, json.errors)
        })
        .catch((error) => {
          console.log(`Failed to lint bloblang field due to: ${error}`)
        })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sessionID]
  )

  const hackInitWidgetRef = useRef<{
    [key: string]: any
  }>({})

  const bloblangWidget = (props: any) => {
    // This is extremely hacky, but the first time onChange is called from a
    // create component modal this seems to reconstruct the entire form and lose
    // focus, so we force it when the form opens instead.
    if (props.value === '' && (hackInitWidgetRef.current || {})[props.id] === undefined) {
      ;(hackInitWidgetRef.current || {})[props.id] = true
      setTimeout(() => {
        props.onChange(' ')
        props.onChange('')
      }, 1)
    }
    return (
      <div className={styles.bloblangWidget}>
        <Editor
          name={`bstdio_field_${props.id}`}
          contents={props.value}
          hideControls={true}
          showGutter={true}
          readOnly={props.disabled}
          mode="coffee"
          onSave={lintBloblang}
          onChange={(contents:any) => props.onChange(contents)}
        ></Editor>
      </div>
    )
  }

  const bigTextWidget = (props: any) => {
    // This is extremely hacky, but the first time onChange is called from a
    // create component modal this seems to reconstruct the entire form and lose
    // focus, so we force it when the form opens instead.
    if (props.value === '' && (hackInitWidgetRef.current || {})[props.id] === undefined) {
      ;(hackInitWidgetRef.current || {})[props.id] = true
      setTimeout(() => {
        props.onChange(' ')
        props.onChange('')
      }, 1)
    }
    return (
      <div className={styles.bigTextWidget}>
        <Editor
          name={`bstdio_field_${props.id}`}
          contents={props.value}
          hideControls={true}
          showGutter={true}
          readOnly={props.disabled}
          mode="text"
          onSave={() => {}}
          onChange={(contents:any) => props.onChange(contents)}
        ></Editor>
      </div>
    )
  }

  const widgets = {
    bloblang: bloblangWidget,
    bigText: bigTextWidget,
  }

  return (
    // @ts-ignore
    <Form
      widgets={widgets}
      schema={schema}
      formData={formData}
      uiSchema={uiSchema}
      onChange={onChange}
      onSubmit={onSubmit}
      disabled={disabled}
      transformErrors={transformErrors}
    >
      {disabled ? (
        <div></div>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'right', gap: '20px' }}>
          <Button variant="contained" color={'secondary'} type="submit">
            Submit
          </Button>
          <Button variant="contained" color="primary" onClick={onCancel}>
            Cancel
          </Button>
        </Box>
      )}
    </Form>
  )
}
